import React from "react";
import { ArrowRight, CheckCheck, PieChart, RefreshCw, TriangleAlert, User, Workflow } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FaBroadcastTower } from "react-icons/fa";
import { TraitConfig } from "../data";
import { Card, InfoChip, SectionHeader, ghostBtn, primaryBtn } from "./ui";

const SYNC: Record<string, { title: string; note: string }> = {
  "sync whenever source value changes": { title: "Value Change", note: "Sync when source value changes" },
  "sync daily": { title: "Daily", note: "Sync once a day" },
  "sync hourly": { title: "Hourly", note: "Sync every hour" },
};

const MiniStat: React.FC<{ icon: React.ReactNode; label: string; value: string; note: string }> = ({ icon, label, value, note }) => (
  <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm p-4">
    <p className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
      {icon} {label}
    </p>
    <p className="text-2xl text-gray-900 dark:text-gray-100 mt-3">{value}</p>
    <p className="text-sm text-gray-700 dark:text-gray-300 mt-3">{note}</p>
  </div>
);

const UseCard: React.FC<{ icon: React.ReactNode; title: string; body: string; cta: string; to: string }> = ({ icon, title, body, cta, to }) => {
  const navigate = useNavigate();
  return (
    <div className="rounded-xl bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 shadow-sm p-4 flex flex-col">
      <p className="flex items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
        {icon} {title}
      </p>
      <p className="text-sm text-gray-700 dark:text-gray-300 mt-3 flex-1">{body}</p>
      <button type="button" onClick={() => navigate(to)} className="mt-3 inline-flex items-center gap-2 text-base text-indigo-700 dark:text-indigo-400 underline underline-offset-2 self-start">
        {cta} <ArrowRight size={18} />
      </button>
    </div>
  );
};

const source = (c: TraitConfig) => (c.category === "Custom" ? c.sourceSystem : c.category === "Behavioral" ? c.event : `${c.goal} model`);

// Everything but the trait's own fields (coverage, last synced, quality) is
// hardcoded until the traits API exists.
const StepSaved: React.FC<{ config: TraitConfig; onEdit: () => void; onCreateNew: () => void; onBackToTraits: () => void }> = ({
  config,
  onEdit,
  onCreateNew,
  onBackToTraits,
}) => {
  const sync = SYNC[config.behavior] || SYNC["sync whenever source value changes"];
  return (
    <div className="space-y-6">
      <SectionHeader
        title={`Saved Trait: ${config.name}`}
        subtitle={`This trait is now synced from ${source(config)} and can be used across customer profiles, segments, journeys and broadcasts`}
      />

      <Card>
        <div className="flex items-center justify-between">
          <p className="text-base text-gray-900 dark:text-gray-100">Trait Summary</p>
          <button type="button" onClick={onEdit} className="text-base text-indigo-700 dark:text-indigo-400 underline underline-offset-2">
            Edit Trait
          </button>
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-4">
          {[
            ["Trait Name", <InfoChip key="n" value={config.name} />],
            [
              "Category",
              <span key="c" className="inline-flex items-center gap-1.5 text-sm px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-100">
                <span className="w-1.5 h-1.5 rounded-full bg-gray-900 dark:bg-gray-100" /> {config.category}
              </span>,
            ],
            ["Source", <InfoChip key="s" value={source(config)} />],
            ["Coverage", <InfoChip key="cv" value="48,320 of 49,000 contacts" />],
            ["Last Synced", <InfoChip key="l" value="4 min ago" />],
            [
              "Status",
              <span key="st" className="inline-flex items-center gap-1.5 text-sm px-2 py-1 rounded bg-green-100 dark:bg-green-500/15 text-gray-800 dark:text-green-300">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Active
              </span>,
            ],
          ].map(([label, value]) => (
            <div key={label as string}>
              <p className="text-sm text-gray-700 dark:text-gray-300">{label}</p>
              <div className="mt-2">{value}</div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniStat icon={<RefreshCw size={22} className="text-green-600" />} label="Sync Behavior" value={sync.title} note={sync.note} />
        <MiniStat icon={<CheckCheck size={22} className="text-green-600" />} label="Data Quality" value="Healthy" note="No issues detected" />
        <MiniStat icon={<TriangleAlert size={22} className="text-rose-500" />} label="Missing values" value="2%" note="1,180 customers" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <UseCard icon={<PieChart size={22} />} title="Segments" body={`Build customer groups using ${config.name}`} cta="Create Segment" to="/segments" />
        <UseCard icon={<Workflow size={22} />} title="Journeys" body={`Trigger journeys based on ${config.name} changes`} cta="Build Journey" to="/journey" />
        <UseCard icon={<FaBroadcastTower size={22} />} title="Broadcasts" body={`Target broadcasts using ${config.name} as an audience filter`} cta="Create Broadcast" to="/campaigns/create" />
        <UseCard icon={<User size={22} />} title="Customer Profiles" body="See the trait value directly inside each customer profile" cta="View Profiles" to="/customers" />
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" className={ghostBtn} onClick={onCreateNew}>
          Create New
        </button>
        <button type="button" className={primaryBtn} onClick={onBackToTraits}>
          Back to Traits
        </button>
      </div>
    </div>
  );
};

export default StepSaved;
